import type { LogBasedError, TriageError } from './types';
import { DEFAULT_RUNBOOK_LINK, runbookLink } from './types';

/**
 * These are produced by our service and should be investigated/fixed.
 * Once the added Linear ticket is done, these should be removed from here.
 */
export const serviceTriageErrors: TriageError[] = [
  {
    messageContains: 'SIGTERM',
    title: 'SIGTERM Logs',
    threshold: 10,
    priority: 5,
    ticket: 'ENG-468',
    notionLink: DEFAULT_RUNBOOK_LINK,
  },
  {
    messageContains: 'error in secret or public key callback',
    title: 'Transient error in live transcription authentication',
    threshold: 10,
    priority: 4,
    ticket: 'ENG-590',
    notionLink: DEFAULT_RUNBOOK_LINK,
  },
  {
    messageContains: 'Error in streaming chat response',
    title: 'Error while streaming chat response',
    threshold: 10,
    priority: 5,
    ticket: 'ENG-1004',
    notionLink: DEFAULT_RUNBOOK_LINK,
  },
  {
    messageContains: 'POST /api/auth/signup',
    threshold: 10,
    priority: 4,
    ticket: 'ENG-1061',
    notionLink: DEFAULT_RUNBOOK_LINK,
  },
  {
    messageContains: 'POST https://api.eluve.com/api/auth/signinup',
    threshold: 10,
    priority: 4,
    ticket: 'ENG-1061',
    notionLink: DEFAULT_RUNBOOK_LINK,
  },
  {
    messageContains: 'POST https://api.eluve.com/api/auth/signup 500',
    threshold: 10,
    priority: 4,
    ticket: 'ENG-1061',
    notionLink: DEFAULT_RUNBOOK_LINK,
  },
  {
    messageContains: 'POST https://api.eluve.com/api/auth/session/refresh',
    threshold: 10,
    priority: 4,
    ticket: 'ENG-1061',
    notionLink: DEFAULT_RUNBOOK_LINK,
  },
  {
    messageContains: 'POST https://api.eluve.com/api/auth/signout 500',
    threshold: 10,
    priority: 4,
    ticket: 'ENG-4182',
    notionLink: DEFAULT_RUNBOOK_LINK,
  },
  {
    title: 'Legacy orphan-candidates request validation',
    messageContains: 'Request Validation Error',
    jsonContains: {
      'data.jsonPayload.path': '*/appointments/orphan-candidates',
    },
    threshold: 10,
    priority: 5,
    evaluationWindow: '15m',
    ticket: 'ENG-4487',
    notionLink: DEFAULT_RUNBOOK_LINK,
  },
  {
    title: 'External chart scoring task failed',
    messageContains: 'capture_external_ehr_note_change_score',
    threshold: 20,
    priority: 5,
    evaluationWindow: '15m',
    ticket: 'ENG-4508',
    notionLink: DEFAULT_RUNBOOK_LINK,
  },
  {
    title: 'External chart scoring task schema exception',
    messageContains: 'EncounterId',
    jsonContains: {
      'data.jsonPayload.stack': '*captureExternalEhrNoteChangeScore*',
    },
    threshold: 20,
    priority: 5,
    evaluationWindow: '15m',
    ticket: 'ENG-4508',
    notionLink: DEFAULT_RUNBOOK_LINK,
  },
  {
    messageContains: 'Groundedness evaluation failed',
    threshold: 10,
    priority: 4,
    ticket: 'ENG-1074',
    notionLink: DEFAULT_RUNBOOK_LINK,
  },
  {
    // Terminal string from llm-task-handler. Matches after Mastra's
    // retryConfig has been exhausted — not on intermediate retry warnings.
    // Soft terminal failures are emitted as warn; only hard terminal failures
    // should page this monitor.
    messageContains: 'Summarization failed, no more retries left',
    status: 'error',
    threshold: 1,
    priority: 4,
    ticket: 'ENG-1075',
    notionLink: runbookLink('87423a50f3a842969de3dc8b332e2f70'),
  },
  {
    messageContains:
      'workflow.summarize-appointment.step.mark-llm-output-as-failed',
    status: 'error',
    threshold: 1,
    priority: 5,
    ticket: 'ENG-4371',
    notionLink: DEFAULT_RUNBOOK_LINK,
  },
  {
    messageContains: 'is out of range for type integer',
    threshold: 20,
    priority: 4,
    ticket: 'ENG-1060',
    notionLink: DEFAULT_RUNBOOK_LINK,
  },
  {
    messageContains: 'Error during LLM completion',
    threshold: 5,
    priority: 4,
    ticket: 'ENG-1063',
    notionLink: runbookLink('dc8237cb23c5444b833906c218faaa61'),
  },
  // Connection-error thresholds sized so a single-instance GCP maintenance
  // connection reset (an expected, recovered condition since the ENG-5034
  // pool hardening; observed 2026-07-16: 12 idle + 33 terminated lines in
  // <3 min) does not trigger, while a multi-instance or sustained failure
  // still crosses within the window. These queries count warn as well as
  // error, so the graphile-worker severity downgrade alone does not quiet
  // them.
  {
    title: 'Postgres: active client generated error',
    messageContains: 'PostgreSQL active client generated error',
    threshold: 40,
    evaluationWindow: '15m',
    priority: 4,
    ticket: 'ENG-1098',
    notionLink: DEFAULT_RUNBOOK_LINK,
  },
  {
    title: 'Postgres: error with notify listener',
    messageContains: 'Error with notify listener',
    threshold: 10,
    priority: 4,
    ticket: 'ENG-1098',
    notionLink: DEFAULT_RUNBOOK_LINK,
  },
  {
    title: 'Postgres: idle client generated error',
    messageContains: 'PostgreSQL idle client generated error',
    threshold: 40,
    evaluationWindow: '15m',
    priority: 4,
    ticket: 'ENG-1098',
    notionLink: DEFAULT_RUNBOOK_LINK,
  },
  {
    title: 'Postgres: generic connection terminated unexpectedly',
    messageContains: 'Connection terminated unexpectedly',
    threshold: 100,
    evaluationWindow: '15m',
    priority: 4,
    ticket: 'ENG-1098',
    notionLink: DEFAULT_RUNBOOK_LINK,
  },
  {
    title: 'Transcription failed',
    messageContains: 'Transcription failed',
    threshold: 1,
    priority: 4,
    ticket: 'ENG-3716',
    notionLink: runbookLink('f2958bc743c24ffebc2e6f38805d5c91'),
  },
  {
    title: 'Failed to transcribe audio',
    messageContains: 'Failed to transcribe audio',
    status: 'error',
    threshold: 1,
    priority: 4,
    ticket: 'ENG-3716',
    notionLink: runbookLink('d70760a2b6344153925fcfa0ca79d4cf'),
  },
  // Siloam location attribution (ENG-5099): every attribution seam is
  // wrapped in try/catch so a failure never breaks login, appointment
  // import, or chart ingest — these monitors are the only way failures
  // surface. Attribution is retried on every launch, so sustained volume
  // (not one-offs) is the signal.
  {
    title: 'Siloam user organization attribution failed',
    messageContains: 'Failed to attribute user organizations',
    status: 'error',
    threshold: 5,
    priority: 4,
    evaluationWindow: '15m',
    ticket: 'ENG-5099',
    notionLink: DEFAULT_RUNBOOK_LINK,
  },
  {
    title: 'Appointment organization location resolution failed',
    messageContains: 'Failed to resolve organization location',
    status: 'error',
    threshold: 5,
    priority: 4,
    evaluationWindow: '15m',
    ticket: 'ENG-5099',
    notionLink: DEFAULT_RUNBOOK_LINK,
  },
  {
    title: 'Chart organization attribution failed',
    messageContains: 'Failed to attribute chart organization',
    status: 'error',
    threshold: 5,
    priority: 4,
    evaluationWindow: '15m',
    ticket: 'ENG-5099',
    notionLink: DEFAULT_RUNBOOK_LINK,
  },
];

/**
 * These are produced by our service, but are not going to be fixed
 * either because they don't have a fix or because we have chosen not to.
 */
export const serviceKnownErrors: LogBasedError[] = [
  {
    messageContains: 'Failed to combine audio files',
    title: 'Audio file combination failed',
    status: 'warn',
    threshold: 5,
    priority: 4,
    notionLink: DEFAULT_RUNBOOK_LINK,
  },
  {
    messageContains: 'Unexpected error summarizing appointment with LLM',
    title: 'LLM Summarization Error',
    threshold: 1,
    priority: 4,
    notionLink: DEFAULT_RUNBOOK_LINK,
  },
  {
    messageContains: 'jwt expired',
    title: 'JWT Expired',
    threshold: 15,
    priority: 4,
    notionLink: runbookLink('20215cf0f89a4768b3fb0fb0c5d3f906'),
  },
  {
    messageContains: 'invalid signature',
    title: 'JWT Invalid Signature',
    threshold: 1,
    priority: 3,
    notionLink: DEFAULT_RUNBOOK_LINK,
  },
  {
    messageContains: 'Transcription initialization timeout',
    threshold: 1,
    priority: 5,
    notionLink: DEFAULT_RUNBOOK_LINK,
  },
  {
    messageContains: 'Failed to stream to GCS',
    threshold: 10,
    priority: 4,
    evaluationWindow: '24h',
    notionLink: DEFAULT_RUNBOOK_LINK,
  },
  {
    // Best-effort upload of the RAW_TRANSCRIPT_METADATA artifact (ENG-5086):
    // a failure no longer blocks note generation, so this monitor is the only
    // remaining visibility into these GCS blips. Fires per occurrence
    // (>= 1 in the default 5m window) on purpose — the same granularity the
    // 'Transcription failed' monitor gave these events before the fix.
    messageContains: 'Failed to save raw transcript JSON to GCS',
    title: 'Raw transcript JSON upload failed',
    threshold: 1,
    priority: 4,
    notionLink: DEFAULT_RUNBOOK_LINK,
  },
  {
    messageContains: 'Langfuse request failed',
    threshold: 3,
    priority: 4,
    notionLink: DEFAULT_RUNBOOK_LINK,
  },
  {
    title:
      'Elevated number of errors when attempting to reach Langfuse service',
    messageContains: 'Langfuse request failed',
    threshold: 10,
    priority: 2,
    evaluationWindow: '24h',
    notionLink: DEFAULT_RUNBOOK_LINK,
  },
  {
    messageContains:
      "SuperTokens core threw an error for a GET request to path: '/public/users/by-accountinfo' with status code: 503",
    title: 'SuperTokens 503 Error on User Lookup',
    threshold: 3,
    priority: 4,
    notionLink: DEFAULT_RUNBOOK_LINK,
  },
  {
    messageContains:
      'POST https://api.siloam.eluve.com/api/auth/signinup/code/consume 500',
    title: 'Siloam Auth Code Consume 500 Error',
    threshold: 3,
    priority: 4,
    notionLink: DEFAULT_RUNBOOK_LINK,
  },
  {
    messageContains: 'Supertokens error',
    status: 'warn',
    title: 'SuperTokens Error',
    threshold: 50,
    priority: 4,
    notionLink: DEFAULT_RUNBOOK_LINK,
  },
  {
    messageContains: 'Failed to parse llm output',
    title: 'Failed to parse LLM output',
    threshold: 5,
    priority: 4,
    notionLink: DEFAULT_RUNBOOK_LINK,
  },
  {
    title: 'Medical Note SLA Breach',
    messageContains: 'SLA breach (error): No medical note available within',
    threshold: 5,
    priority: 3,
    evaluationWindow: '5m',
    notionLink: DEFAULT_RUNBOOK_LINK,
  },
  {
    messageContains: 'Failed to generate LLM output',
    title: 'Failed to generate LLM output',
    threshold: 5,
    priority: 4,
    notionLink: DEFAULT_RUNBOOK_LINK,
  },
  {
    messageContains: 'File not found',
    title: 'File not found during file access',
    threshold: 10,
    priority: 4,
    notionLink: DEFAULT_RUNBOOK_LINK,
  },
  {
    messageContains: 'not allowed by CORS',
    title: 'CORS Origin Rejected',
    threshold: 50,
    priority: 4,
    notionLink: DEFAULT_RUNBOOK_LINK,
  },
  {
    messageContains: 'Gemini transcription failed',
    title: 'Gemini Transcription Failed',
    status: 'error',
    threshold: 1,
    priority: 4,
    notionLink: DEFAULT_RUNBOOK_LINK,
  },
  {
    messageContains: 'not found, reached max attempts',
    title: 'Langfuse Observation Not Found After Max Retries',
    threshold: 10,
    priority: 4,
    evaluationWindow: '15m',
    notionLink: DEFAULT_RUNBOOK_LINK,
  },
  {
    messageContains: 'LLM output data not found',
    title: 'LLM output scoring: output row regenerated before scoring task ran',
    threshold: 10,
    priority: 4,
    evaluationWindow: '15m',
    notionLink: DEFAULT_RUNBOOK_LINK,
  },
  {
    messageContains: 'Failed to decrypt PDF file',
    title: 'Elevated chat PDF decrypt failures',
    threshold: 20,
    priority: 4,
    evaluationWindow: '15m',
    notionLink: DEFAULT_RUNBOOK_LINK,
  },
];

/**
 * These are produced by the system outside of our service and should be investigated/fixed.
 * Once the added Linear ticket is done, these should be removed from here.
 */
export const systemTriageErrors: TriageError[] = [];

/**
 * These are produced by the system outside of our service, but are not going to
 * be fixed either because they don't have a fix or because we have chosen not to.
 */
export const systemKnownErrors: LogBasedError[] = [
  {
    messageContains:
      'remaining connection slots are reserved for non-replication superuser connections',
    title: 'Database Connection Slots Error',
    threshold: 1,
    priority: 1,
    notionLink: DEFAULT_RUNBOOK_LINK,
  },
  {
    messageContains: 'Health Check has failed!',
    title: 'Health Check Failed',
    threshold: 1,
    priority: 1,
    notionLink: DEFAULT_RUNBOOK_LINK,
  },
  {
    messageContains: 'LIVENESS HTTP probe',
    title: 'Liveness Probe Failed',
    threshold: 1,
    priority: 2,
    notionLink: DEFAULT_RUNBOOK_LINK,
  },
  {
    messageContains:
      'The request failed because either the HTTP response was malformed or connection to the instance had an error',
    title: 'Cloud Run Malformed HTTP Response / Connection Error',
    threshold: 5,
    priority: 4,
    evaluationWindow: '15m',
    notionLink: DEFAULT_RUNBOOK_LINK,
  },
];
